/**
 * SessionDetailPage - Individual session detail with roster and controls
 * Displays session information, player roster, and action buttons
 * Shows TeacherGameMonitor when session is LIVE
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { sessionsService } from '@/services/sessions';
import { classesService } from '@/services/classes';
import { SessionWithPlayers, SessionStatus } from '@/types/session';
import { ClassWithMetrics } from '@/types/class';
import { TestModeButton } from '@/components/TestModeButton';
import { TeacherGameMonitor } from '@/game/components/TeacherGameMonitor';
import { MoveSessionModal } from '@/components/MoveSessionModal';
import { adaptGameStateResponse, createMinimalGameState } from '@/game/utils/gameStateAdapter';
import { GameState } from '@/game/models/GameState';
import { getPlayerIcon } from '@/config/playerIcons';

export function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<SessionWithPlayers | null>(null);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [classes, setClasses] = useState<ClassWithMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showGameMonitor, setShowGameMonitor] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [kickingPlayerId, setKickingPlayerId] = useState<string | null>(null);

  // Initial load
  useEffect(() => {
    loadSession();
  }, [sessionId]);

  // Auto-refresh when session is in WAITING status
  useEffect(() => {
    if (!session || session.status !== SessionStatus.WAITING) {
      return;
    }

    const intervalId = setInterval(() => {
      refreshSession();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(intervalId);
  }, [session?.status, sessionId]);

  // Fetch game state when session is LIVE or COMPLETE
  useEffect(() => {
    if (!session || !sessionId) return;
    if (session.status !== SessionStatus.LIVE && session.status !== SessionStatus.COMPLETE) {
      return;
    }

    async function fetchGameState() {
      try {
        const response = await sessionsService.getGameState(sessionId!);
        const adapted = adaptGameStateResponse(response);
        if (adapted) {
          setGameState(adapted);
        } else {
          // Create minimal game state from session data if full state not available
          setGameState(createMinimalGameState(sessionId!, session!.players));
        }
      } catch (err) {
        console.error('Failed to fetch game state:', err);
        // Fall back to minimal state from session data
        setGameState(createMinimalGameState(sessionId!, session!.players));
      }
    }

    fetchGameState();

    // Auto-refresh game state every 5 seconds when LIVE (not needed for COMPLETE)
    if (session.status === SessionStatus.LIVE) {
      const intervalId = setInterval(fetchGameState, 5000);
      return () => clearInterval(intervalId);
    }
  }, [session?.status, sessionId, session?.players]);

  async function loadSession() {
    if (!sessionId) {
      setError('Session ID is missing');
      setLoading(false);
      return;
    }

    // Load session and classes
    try {
      setLoading(true);
      setError(null);

      const [sessionData, classesData] = await Promise.all([
        sessionsService.getSession(sessionId),
        classesService.listClasses(),
      ]);

      setSession(sessionData);
      setClasses(classesData);
    } catch (err: any) {
      console.error('Failed to load session:', err);
      setError(err.response?.data?.message || 'Failed to load session');
    } finally {
      setLoading(false);
    }
  }

  function getClassName(classId?: string): string {
    if (!classId) return 'Uncategorized';
    const classItem = classes.find((c) => c.classId === classId);
    return classItem?.className || 'Unknown';
  }

  async function handleMoveSession(classId: string | null) {
    if (!sessionId || !session) return;

    await sessionsService.moveSessionToClass(sessionId, classId);
    await loadSession(); // Refresh session data
  }

  // Silent refresh without loading spinner (for auto-refresh)
  async function refreshSession() {
    if (!sessionId) return;

    try {
      const data = await sessionsService.getSession(sessionId);
      setSession(data);
    } catch (err: any) {
      // Silently fail on auto-refresh errors to avoid disrupting the user
      console.error('Failed to refresh session:', err);
    }
  }

  async function handleStartSession() {
    if (!sessionId || !session) return;

    // Validate that we have enough players
    if (session.joinedCount < session.requiredPlayers) {
      setActionError(
        `Cannot start session: Need ${session.requiredPlayers} players, only ${session.joinedCount} joined`
      );
      return;
    }

    try {
      setActionLoading(true);
      setActionError(null);

      const response = await sessionsService.startSession(sessionId);

      // Update local session state with new status
      setSession({
        ...session,
        status: response.status,
        startedAt: response.startedAt,
      });

      // Show success message briefly
      setTimeout(() => {
        loadSession(); // Reload to get updated player roles
      }, 500);
    } catch (err: any) {
      console.error('Failed to start session:', err);
      setActionError(
        err.response?.data?.message || 'Failed to start session'
      );
    } finally {
      setActionLoading(false);
    }
  }

  async function handleEndSession() {
    if (!sessionId || !session) return;

    const confirmed = window.confirm(
      'Are you sure you want to end this session? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      setActionError(null);

      const response = await sessionsService.completeSession(sessionId);

      // Update local session state
      setSession({
        ...session,
        status: response.status,
        completedAt: response.completedAt,
      });

      // Navigate to results after a brief delay
      setTimeout(() => {
        navigate(`/login/teacher/sessions/${sessionId}/results`);
      }, 1500);
    } catch (err: any) {
      console.error('Failed to end session:', err);
      setActionError(err.response?.data?.message || 'Failed to end session');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleKickPlayer(playerId: string, displayName: string) {
    if (!sessionId) return;

    const confirmed = window.confirm(
      `Are you sure you want to remove "${displayName}" from this session?`
    );
    if (!confirmed) return;

    try {
      setKickingPlayerId(playerId);
      await sessionsService.removePlayer(sessionId, playerId);
      await loadSession();
    } catch (err: any) {
      console.error('Failed to remove player:', err);
      setActionError(err.message || 'Failed to remove player');
    } finally {
      setKickingPlayerId(null);
    }
  }

  function renderActionBar() {
    if (!session) return null;

    const { status, joinedCount, requiredPlayers } = session;

    if (status === SessionStatus.WAITING) {
      const canStart = joinedCount >= requiredPlayers;
      return (
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-4 mb-6 flex items-center justify-between">
          <button
            onClick={handleStartSession}
            disabled={!canStart || actionLoading}
            className="px-6 py-3 bg-[#367A25] text-white font-semibold rounded-lg hover:bg-[#2d6820] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {actionLoading ? 'Starting...' : 'Start Session'}
          </button>
          <div className="flex items-center gap-4">
            {!canStart && (
              <p className="text-sm text-slate-600">
                Waiting for {requiredPlayers - joinedCount} more player(s) to
                join
              </p>
            )}
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#367A25] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#367A25]"></span>
              </span>
              Auto-refreshing
            </span>
          </div>
        </div>
      );
    }

    if (status === SessionStatus.LIVE) {
      return (
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowGameMonitor(!showGameMonitor)}
              className={`px-6 py-3 font-semibold rounded-lg transition-colors ${
                showGameMonitor
                  ? 'bg-[#12356B] text-white hover:bg-[#0d2850]'
                  : 'bg-[#E2E5EC] text-[#12356B] hover:bg-[#CED4DE]'
              }`}
            >
              {showGameMonitor ? 'Hide Game Monitor' : 'Show Game Monitor'}
            </button>
            <button
              onClick={handleEndSession}
              disabled={actionLoading}
              className="px-6 py-3 bg-[#DD6832] text-white font-semibold rounded-lg hover:bg-[#c4562a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Ending...' : 'End Session'}
            </button>
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2 text-sm text-[#367A25] font-medium">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#367A25] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#367A25]"></span>
              </span>
              Session in progress
            </span>
          </div>
        </div>
      );
    }

    if (status === SessionStatus.COMPLETE) {
      return (
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-4 mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate(`/login/teacher/sessions/${sessionId}/results`)}
            className="px-6 py-3 bg-[#367A25] text-white font-semibold rounded-lg hover:bg-[#2d6820] transition-colors"
          >
            View Results
          </button>
          <p className="text-sm text-slate-600">Session completed</p>
        </div>
      );
    }

    return null;
  }

  function getStatusClass(status: SessionStatus): string {
    switch (status) {
      case SessionStatus.WAITING:
        return 'bg-[#DD6832]/12 text-[#c4562a]';
      case SessionStatus.LIVE:
        return 'bg-[#367A25]/12 text-[#367A25]';
      case SessionStatus.COMPLETE:
        return 'bg-[#12356B]/12 text-[#12356B]';
      default:
        return 'bg-[#E2E5EC] text-[#12356B]';
    }
  }

  function formatDateTime(isoString: string | undefined): string {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#CED4DE] border-t-[#12356B]" />
            <p className="mt-4 text-[#12356B]">Loading session details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-xl shadow-sm px-6 py-4 mb-6">
            <strong className="text-[#c4562a]">Error:</strong> <span className="text-[#c4562a]">{error || 'Session not found'}</span>
          </div>
          <button
            onClick={() => navigate('/login/teacher')}
            className="px-6 py-3 border border-[#12356B] text-[#12356B] bg-white rounded-lg hover:bg-[#E2E5EC] transition-colors font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Auto-Closed Warning Banner */}
        {session.closureReason === 'AUTO_CLOSED_TIMEOUT' && (
          <div className="bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-xl shadow-sm px-6 py-4 mb-6">
            <p className="font-semibold text-[#c4562a]">Session Auto-Closed</p>
            <p className="text-sm text-[#c4562a]">
              This session was automatically closed after being live for more than 24 hours.
            </p>
          </div>
        )}

        {/* Session Header */}
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <label className="text-xs text-slate-600 font-medium block mb-1">Session Name</label>
              <h1 className="text-3xl font-bold text-[#12356B] mb-2">
                {session.sessionName || `Session ${session.code}`}
              </h1>
              <div className="flex items-center gap-8">
                <span className="px-3 py-1 bg-[#367A25]/10 text-[#367A25] text-sm font-medium rounded-lg">
                  {getClassName(session.classId)}
                </span>
                <button
                  onClick={() => setIsMoveModalOpen(true)}
                  className="px-3 py-1 bg-[#DD6832] text-white rounded-lg hover:bg-[#c4562a] transition-colors text-sm font-medium">
                  Move to class
                </button>
              </div>
            </div>
            <span className={`px-4 py-2 rounded-lg font-semibold text-sm ${getStatusClass(session.status)}`}>
              {session.status}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
              <label className="text-xs text-slate-600 font-medium block mb-1">Join Code</label>
              <p className="text-2xl font-bold text-[#12356B]">{session.code}</p>
            </div>

            <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
              <label className="text-xs text-slate-600 font-medium block mb-1">Players</label>
              <p className="text-lg font-semibold text-[#12356B]">
                {session.joinedCount} / {session.requiredPlayers}
              </p>
            </div>

            <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
              <label className="text-xs text-slate-600 font-medium block mb-1">Created</label>
              <p className="text-sm text-[#12356B]">{formatDateTime(session.createdAt)}</p>
            </div>

            {session.startedAt && (
              <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
                <label className="text-xs text-slate-600 font-medium block mb-1">Started</label>
                <p className="text-sm text-[#12356B]">{formatDateTime(session.startedAt)}</p>
              </div>
            )}

            {session.completedAt && (
              <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
                <label className="text-xs text-slate-600 font-medium block mb-1">Completed</label>
                <p className="text-sm text-[#12356B]">{formatDateTime(session.completedAt)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Test Mode Button (developer only) */}
        <TestModeButton
          sessionId={session.sessionId}
          sessionCode={session.code}
          sessionStatus={session.status}
          players={session.players}
          onPlayersJoined={loadSession}
        />

        {/* Action Bar */}
        {actionError && (
          <div className="bg-[#DD6832]/10 border border-[#DD6832]/30 rounded-xl shadow-sm px-6 py-4 mb-6">
            <strong className="text-[#c4562a]">Error:</strong> <span className="text-[#c4562a]">{actionError}</span>
          </div>
        )}
        {renderActionBar()}

        {/* Game Monitor (when session is LIVE) */}
        {session.status === SessionStatus.LIVE && showGameMonitor && gameState && (
          <div className="mb-6">
            <TeacherGameMonitor
              gameState={gameState}
              className="w-full"
            />
          </div>
        )}

        {/* Player Roster */}
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-6 mb-6">
          <h2 className="text-2xl font-bold text-[#12356B] mb-4">Player Roster</h2>

          {session.players.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 mb-2">No players have joined yet</p>
              <p className="text-sm text-slate-500">
                Share the join code <strong className="text-[#12356B]">{session.code}</strong> with students
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#CED4DE]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">#</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Display Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Full Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Energy</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Joined At</th>
                    {session.status === SessionStatus.WAITING && (
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {session.players.map((player, index) => (
                    <tr key={player.playerId} className="border-b border-[#E2E5EC] hover:bg-[#E2E5EC]/40">
                      <td className="py-3 px-4 text-sm text-slate-900">{index + 1}</td>
                      <td className="py-3 px-4 text-sm font-medium text-[#12356B]">{player.displayName}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{player.fullName || '—'}</td>
                      <td className="py-3 px-4">
                        {player.role ? (
                          <div className="flex items-center gap-2">
                            {getPlayerIcon(player.role) && (
                              <img
                                src={getPlayerIcon(player.role)!}
                                alt={`${player.role} icon`}
                                className="w-6 h-6 object-contain"
                              />
                            )}
                            <span className="px-3 py-1 bg-[#12356B]/10 text-[#12356B] text-xs font-semibold rounded-lg">{player.role}</span>
                          </div>
                        ) : (
                          <span className="px-3 py-1 bg-[#E2E5EC] text-slate-600 text-xs rounded-lg">Not assigned</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900">{player.energy}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{formatDateTime(player.joinedAt)}</td>
                      {session.status === SessionStatus.WAITING && (
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleKickPlayer(player.playerId, player.displayName)}
                            disabled={kickingPlayerId === player.playerId}
                            className="px-3 py-1 bg-[#DD6832]/12 text-[#c4562a] text-xs font-semibold rounded-lg hover:bg-[#DD6832]/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {kickingPlayerId === player.playerId ? 'Removing...' : 'Remove'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Session Configuration */}
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm px-6 py-6 mb-6">
          <h3 className="text-xl font-bold text-[#12356B] mb-4">Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
              <label className="text-xs text-slate-600 font-medium block mb-1">Required Players</label>
              <p className="text-lg font-semibold text-[#12356B]">{session.requiredPlayers}</p>
            </div>
            <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
              <label className="text-xs text-slate-600 font-medium block mb-1">Late Joins Allowed</label>
              <p className="text-lg font-semibold text-[#12356B]">{session.allowLateJoins ? 'Yes' : 'No'}</p>
            </div>
            <div className="bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg px-4 py-3">
              <label className="text-xs text-slate-600 font-medium block mb-1">Character Pool</label>
              <p className="text-sm text-[#12356B]">{session.characterPool?.join(', ') || 'Default'}</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="flex justify-start">
          <button
            onClick={() => navigate('/login/teacher')}
            className="px-6 py-3 border border-[#12356B] text-[#12356B] bg-white rounded-lg hover:bg-[#E2E5EC] transition-colors font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Move Session Modal */}
      {session && (
        <MoveSessionModal
          isOpen={isMoveModalOpen}
          onClose={() => setIsMoveModalOpen(false)}
          session={session}
          onMove={handleMoveSession}
        />
      )}
    </div>
  );
}
